<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import type { IBulkOutcome } from '@/composition/catalog/use-catalog-writes';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { useToastObstacle } from '@/composition/use-toast-obstacle';
  import { IAvailabilityOp } from '@/interfaces/ICatalogV2';
  import { IItem } from '@/interfaces/IItem';

  /**
   * The selection's actions (plan §5.10): «Ocultar» (with the branch scope
   * in multi-branch stores), «Mostrar», «Mover a…», «Cambiar precio» and
   * ⋯ «Eliminar». Fixed at the bottom right, dark; hidden while a panel or
   * an editor is open (it would cover the editor's «Guardar»). Applied
   * products leave the selection.
   */
  /** Labels sit beside the icons from this window width (px). */
  const WIDE_WINDOW_PX = 720;

  const {
    state,
    data,
    access,
    online,
    layout,
    actions,
    commands,
  } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const root = ref<HTMLElement | null>(null);

  const { width: windowWidth } = useStableWindowSize();
  const phone = computed(() => layout.tier.value === 'phone');
  const wide = computed(
    () => !phone.value && windowWidth.value >= WIDE_WINDOW_PX
  );
  const busy = ref(false);

  const selectedItems = computed<IItem[]>(() => {
    const byId = data.itemById.value;
    return Object.keys(state.selectedIds).flatMap((id) => {
      const item = byId[Number(id)];
      return item ? [item] : [];
    });
  });
  const count = computed(() => selectedItems.value.length);

  const shown = computed(
    () =>
      state.mode === 'select' &&
      count.value > 0 &&
      !state.panel &&
      !state.editor
  );
  // Toasts avoid the bar only while it shows: measuring the hidden bar
  // would force a layout while the page is still being built.
  useToastObstacle(computed(() => (shown.value ? root.value : null)));

  const multiBranch = computed(() => data.activeStoreBranches.value.length > 1);
  const branchName = computed(() => {
    const id = data.branchId.value;
    return (
      data.storeBranches.value.find((branch) => branch.id === id)?.name ?? ''
    );
  });
  const writeTitle = computed(() =>
    online.value ? undefined : t('menuPage.errors.offline')
  );
  const planPath = computed(() => {
    const stringId = data.restaurant.value?.stringId;
    return stringId ? `/${stringId}/settings?tab=plan` : null;
  });

  function deselect(ids: readonly number[]) {
    if (!ids.length) return;
    const next = { ...state.selectedIds };
    for (const id of ids) delete next[id];
    state.selectedIds = next;
  }

  /** One action at a time; the bar stays mounted (`v-show`) across it. */
  async function run(action: () => Promise<IBulkOutcome>) {
    if (busy.value || !count.value) return;
    busy.value = true;
    try {
      deselect((await action()).appliedIds);
    } finally {
      busy.value = false;
    }
  }

  function hide(op: IAvailabilityOp, close?: () => void) {
    close?.();
    const items = selectedItems.value;
    void run(() => actions.hideItems(items, op));
  }

  /** Single-branch stores hide at once; the scope asks otherwise. */
  function hideDirect() {
    const branchId = data.branchId.value;
    hide(
      access.value.canHideEverywhere || branchId === null
        ? { visible: false }
        : { hideInBranchIds: [branchId] }
    );
  }

  function hideHere(close: () => void) {
    const branchId = data.branchId.value;
    if (branchId !== null) hide({ hideInBranchIds: [branchId] }, close);
  }

  function show() {
    const items = selectedItems.value;
    void run(() => actions.showItems(items));
  }

  function move() {
    const items = selectedItems.value;
    void run(() => commands.moveProducts(items));
  }

  function changePrice() {
    const items = selectedItems.value;
    void run(() => commands.changeBulkPrices(items));
  }

  function remove(close: () => void) {
    close();
    const items = selectedItems.value;
    void run(() => actions.deleteItems(items));
  }
</script>

<template>
  <div
    v-show="shown"
    ref="root"
    class="v2-catalog-selection-bar"
    :class="{ 'is-wide': wide, 'is-phone': phone }"
    role="toolbar"
    :aria-label="tc('menuPage.select.count', count, { n: count })"
  >
    <template v-if="access.canToggle">
      <PosUiPopover
        v-if="multiBranch"
        class="bar-popover"
        placement="top"
        align="start"
      >
        <template #trigger="{ toggle }">
          <button
            type="button"
            class="bar-action"
            :disabled="busy || !online"
            :title="writeTitle"
            @click="toggle"
          >
            <i class="mdi mdi-eye-off-outline action-icon" aria-hidden="true" />
            <span class="action-label">{{ t('menuPage.bulk.hide') }}</span>
          </button>
        </template>
        <template #default="{ close }">
          <div class="bar-menu">
            <button type="button" class="menu-entry" @click="hideHere(close)">
              {{ t('menuPage.availability.hideHere', { branch: branchName }) }}
            </button>
            <button
              v-if="access.canHideEverywhere"
              type="button"
              class="menu-entry"
              @click="hide({ visible: false }, close)"
            >
              {{ t('menuPage.availability.hideEverywhere') }}
            </button>
          </div>
        </template>
      </PosUiPopover>
      <button
        v-else
        type="button"
        class="bar-action"
        :disabled="busy || !online"
        :title="writeTitle"
        @click="hideDirect"
      >
        <i class="mdi mdi-eye-off-outline action-icon" aria-hidden="true" />
        <span class="action-label">{{ t('menuPage.bulk.hide') }}</span>
      </button>

      <button
        type="button"
        class="bar-action"
        :disabled="busy || !online"
        :title="writeTitle"
        @click="show"
      >
        <i class="mdi mdi-eye-outline action-icon" aria-hidden="true" />
        <span class="action-label">{{ t('menuPage.bulk.show') }}</span>
      </button>
    </template>

    <template v-if="access.canManage">
      <button
        type="button"
        class="bar-action"
        :disabled="busy || !online"
        :title="writeTitle"
        @click="move"
      >
        <i class="mdi mdi-folder-move-outline action-icon" aria-hidden="true" />
        <span class="action-label">{{ t('menuPage.bulk.move') }}</span>
      </button>

      <button
        v-if="access.canBulkPrice"
        type="button"
        class="bar-action"
        :disabled="busy || !online"
        :title="writeTitle"
        @click="changePrice"
      >
        <i class="mdi mdi-currency-usd action-icon" aria-hidden="true" />
        <span class="action-label">{{ t('menuPage.bulk.price') }}</span>
      </button>
      <PosUiPopover
        v-else-if="access.upsellBulkPrice"
        class="bar-popover"
        placement="top"
        align="end"
      >
        <template #trigger="{ toggle }">
          <button type="button" class="bar-action" @click="toggle">
            <i class="mdi mdi-currency-usd action-icon" aria-hidden="true" />
            <span class="action-label">{{ t('menuPage.bulk.price') }}</span>
          </button>
        </template>
        <template #default>
          <p class="bar-upsell">
            {{ t('menuPage.upsell.plus') }} ·
            <router-link v-if="planPath" :to="planPath" class="upsell-link">{{
              t('menuPage.upsell.cta')
            }}</router-link>
          </p>
        </template>
      </PosUiPopover>

      <PosUiPopover class="bar-popover" placement="top" align="end">
        <template #trigger="{ toggle }">
          <button
            type="button"
            class="bar-action is-more"
            :aria-label="t('menuPage.more.title')"
            :title="t('menuPage.more.title')"
            :disabled="busy"
            @click="toggle"
          >
            <i class="mdi mdi-dots-horizontal action-icon" aria-hidden="true" />
          </button>
        </template>
        <template #default="{ close }">
          <div class="bar-menu">
            <button
              type="button"
              class="menu-entry is-danger"
              :disabled="!online"
              @click="remove(close)"
            >
              <i class="mdi mdi-delete-outline entry-icon" aria-hidden="true" />
              {{ t('menuPage.bulk.delete') }}
            </button>
          </div>
        </template>
      </PosUiPopover>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-selection-bar {
    @apply tw-fixed tw-flex tw-items-stretch tw-rounded-2xl tw-bg-neutral-900 tw-p-1 tw-text-on-inverse tw-shadow-lg;
    // Left of the docked «Asistente» panel (0 px otherwise).
    right: calc(16px + var(--assistant-inset-right, 0px));
    bottom: calc(16px + env(safe-area-inset-bottom));
    max-width: calc(100vw - 32px - var(--assistant-inset-right, 0px));
    z-index: 30;

    // Phones: the whole width.
    &.is-phone {
      // Right of the permanent drawer while the panel is docked (0 px otherwise).
      left: calc(12px + var(--assistant-inset-left, 0px));
      right: calc(12px + var(--assistant-inset-right, 0px));
      max-width: none;
    }

    .bar-popover {
      @apply tw-flex tw-min-w-0 tw-flex-1;
    }

    .bar-action {
      @apply tw-flex tw-min-h-[56px] tw-min-w-0 tw-flex-1 tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border-0 tw-bg-transparent tw-px-3 tw-text-on-inverse;

      &:hover:not(:disabled) {
        @apply tw-bg-on-inverse/10;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      &:disabled {
        @apply tw-cursor-not-allowed tw-opacity-50;
      }

      &.is-more {
        @apply tw-flex-none tw-px-2;
      }

      .action-icon {
        @apply tw-text-xl tw-leading-6;
      }

      // Shared widths on phones: a long label takes two lines.
      .action-label {
        @apply tw-mt-0.5 tw-text-center tw-text-[11px] tw-font-medium tw-leading-[13px];
      }
    }

    // Wide: the bar sizes to its actions (a shared zero basis would shrink
    // it below its labels).
    &.is-wide {
      .bar-popover {
        @apply tw-flex-none;
      }

      .bar-action {
        @apply tw-min-h-[48px] tw-flex-none tw-flex-row;

        .action-label {
          @apply tw-ml-2 tw-mt-0 tw-whitespace-nowrap tw-text-sm;
        }
      }
    }

    .bar-menu {
      @apply tw-flex tw-flex-col tw-p-1;
      min-width: 240px;

      .menu-entry {
        @apply tw-flex tw-min-h-[44px] tw-w-full tw-cursor-pointer tw-items-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-text-left tw-text-sm tw-text-neutral-900;

        &:hover:not(:disabled) {
          @apply tw-bg-neutral-100;
        }

        &:disabled {
          @apply tw-cursor-not-allowed tw-text-neutral-400;
        }

        &.is-danger {
          @apply tw-text-red-700;
        }

        .entry-icon {
          @apply tw-mr-2 tw-text-lg;
        }
      }
    }

    .bar-upsell {
      @apply tw-m-0 tw-p-3 tw-text-sm tw-text-neutral-700;
      min-width: 220px;

      .upsell-link {
        @apply tw-font-semibold tw-text-neutral-900 tw-underline;
      }
    }
  }
</style>
